import React from 'react';
import {
  Box, Grid, TextField, Typography, FormControlLabel, Checkbox,
  Select, MenuItem, InputLabel, FormControl, Chip, Autocomplete,
  Divider, FormGroup, LinearProgress
} from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import { publicScreeningAPI } from '../../../services/api';

// entry_points.type values for each option of the entry type select
const ENTRY_TYPE_TO_PORT_TYPE: Record<string, string | undefined> = {
  airport: 'Air-Port',
  port: 'Sea-Port',
  land_border: 'Land-Port',
};

const ALL_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Angola','Argentina','Australia','Austria',
  'Bangladesh','Belgium','Bolivia','Brazil','Burundi',
  'Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Congo',
  'Democratic Republic of Congo','Denmark','Djibouti','Dominican Republic',
  'Ecuador','Egypt','Ethiopia',
  'Finland','France',
  'Germany','Ghana','Guinea',
  'Haiti','Honduras',
  'India','Indonesia','Iraq','Ireland','Italy',
  'Japan','Jordan',
  'Kenya',
  'Lebanon','Liberia','Libya',
  'Madagascar','Malawi','Mali','Mexico','Morocco','Mozambique','Myanmar',
  'Netherlands','Niger','Nigeria','North Korea','Norway',
  'Pakistan','Peru','Philippines','Poland','Portugal',
  'Republic of Congo','Romania','Russia','Rwanda',
  'Saudi Arabia','Senegal','Sierra Leone','Somalia','South Africa',
  'South Sudan','Spain','Sudan','Sweden','Switzerland','Syria',
  'Tanzania','Thailand','Togo','Tunisia','Turkey',
  'Uganda','Ukraine','United Kingdom','United States',
  'Zambia','Zimbabwe',
];

const HIGH_RISK = new Set([
  'Angola','Burundi','Central African Republic','Republic of Congo','Ethiopia',
  'Kenya','Rwanda','South Sudan','Tanzania','Zambia',
  'Democratic Republic of Congo','Uganda',
]);

interface Props {
  form: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
  subTab: number;
  subTabLabels: string[];
}

const HealthFormStep: React.FC<Props> = ({ form, onChange, subTab, subTabLabels }) => {
  const tab = subTab;

  const bool = (key: string) => Boolean(form[key]);
  const str  = (key: string) => (form[key] as string) ?? '';

  const symptomFields = [
    { key: 'symptom_fever',     label: 'Fever' },
    { key: 'symptom_cough',     label: 'Cough' },
    { key: 'symptom_breathing', label: 'Difficulty breathing' },
    { key: 'symptom_vomiting',  label: 'Vomiting' },
    { key: 'symptom_diarrhea',  label: 'Diarrhea' },
    { key: 'symptom_jaundice',  label: 'Jaundice' },
    { key: 'symptom_rash',      label: 'Skin rash' },
    { key: 'symptom_bleeding',  label: 'Unusual bleeding' },
  ];

  // Points of entry come from the entry_points table (same list as the public form)
  const [entryCities, setEntryCities] = React.useState<{ id: string; title: string }[]>([]);
  const entryType = str('entry_type');
  React.useEffect(() => {
    publicScreeningAPI.entryOptions(ENTRY_TYPE_TO_PORT_TYPE[entryType])
      .then((d: { entry_cities?: { id: string; title: string }[] }) => setEntryCities(d.entry_cities || []))
      .catch(() => setEntryCities([]));
  }, [entryType]);

  const progress = ((tab + 1) / subTabLabels.length) * 100;

  return (
    <Box>
      {/* Section header + progress */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {subTabLabels[tab]}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({tab + 1} of {subTabLabels.length})
          </Typography>
        </Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5, borderRadius: 1, height: 4 }} />
      </Box>

      {/* ── Entry Info ── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Point of Entry Type</InputLabel>
              <Select value={str('entry_type')} label="Point of Entry Type" onChange={e => { onChange('entry_type', e.target.value); onChange('entry_city', ''); }}>
                <MenuItem value="airport">Airport</MenuItem>
                <MenuItem value="port">Port</MenuItem>
                <MenuItem value="land_border">Land border crossing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" select required label="Point of Entry"
              value={str('entry_city')} onChange={e => onChange('entry_city', e.target.value)}
              error={!str('entry_city')} helperText={!str('entry_city') ? 'Required' : ' '}>
              {entryCities.map(c => <MenuItem key={c.id} value={c.title}>{c.title}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Country of Entry" value={str('entry_country')} onChange={e => onChange('entry_country', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Date of Arrival" type="date" value={str('arrival_date')} onChange={e => onChange('arrival_date', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Arrival Time" type="time" value={str('arrival_time')} onChange={e => onChange('arrival_time', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Flight / Ship / Vehicle Number" value={str('transport_number')} onChange={e => onChange('transport_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Country of Immediate Origin" value={str('country_of_origin')} onChange={e => onChange('country_of_origin', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={ALL_COUNTRIES}
              value={(form.countries_visited_21d as string[]) || []}
              onChange={(_, v) => onChange('countries_visited_21d', v)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Countries visited in the last 21 days" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, i) => (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={opt}
                    label={opt}
                    size="small"
                  />
                ))
              }
            />
          </Grid>
        </Grid>
      )}

      {/* ── Identity ── */}
      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Full Name" value={str('full_name')} onChange={e => onChange('full_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Sex</InputLabel>
              <Select value={str('sex')} label="Sex" onChange={e => onChange('sex', e.target.value)}>
                <MenuItem value="M">Male</MenuItem>
                <MenuItem value="F">Female</MenuItem>
                <MenuItem value="X">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Date of Birth" type="date" value={str('date_of_birth')} onChange={e => onChange('date_of_birth', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Age" type="number" value={str('age')} onChange={e => onChange('age', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Nationality" value={str('nationality')} onChange={e => onChange('nationality', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>ID Type</InputLabel>
              <Select value={str('id_type')} label="ID Type" onChange={e => onChange('id_type', e.target.value)}>
                <MenuItem value="passport">Passport</MenuItem>
                <MenuItem value="id_card">Identity card</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="ID / Passport Number" value={str('id_number')} onChange={e => onChange('id_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Phone Number" value={str('phone_number')} onChange={e => onChange('phone_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Email" type="email" value={str('email')} onChange={e => onChange('email', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Purpose of Travel" value={str('travel_purpose')} onChange={e => onChange('travel_purpose', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Place of Residence in Host Country" value={str('residence_host_country')} onChange={e => onChange('residence_host_country', e.target.value)} />
          </Grid>
        </Grid>
      )}

      {/* ── Health ── */}
      {tab === 2 && (
        <Box>
          {/* Temperature */}
          <Typography variant="subtitle2" gutterBottom>Current Health Status</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThermostatIcon color="action" />
              <TextField
                size="small"
                label="Measured Temperature (°C)"
                type="number"
                value={str('temperature')}
                onChange={e => onChange('temperature', e.target.value)}
                inputProps={{ step: 0.1, min: 34, max: 45 }}
                helperText="Optional"
                sx={{ width: 220 }}
              />
            </Box>
            <TextField
              size="small"
              label="Telephone"
              value={str('phone_number')}
              onChange={e => onChange('phone_number', e.target.value)}
              helperText="Optional"
              sx={{ width: 200 }}
            />
            <TextField
              size="small"
              label="Email"
              type="email"
              value={str('email')}
              onChange={e => onChange('email', e.target.value)}
              helperText="Optional"
              sx={{ width: 220 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Current Symptoms</Typography>
          <FormGroup>
            <Grid container>
              {symptomFields.map(({ key, label }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    control={<Checkbox checked={bool(key)} onChange={e => onChange(key, e.target.checked)} color="error" />}
                    label={label}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small"
                  label="Other symptoms (specify)"
                  value={str('symptom_other')}
                  onChange={e => onChange('symptom_other', e.target.value)}
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
          </FormGroup>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Recent Exposure</Typography>
          <FormControlLabel
            control={<Checkbox checked={bool('contact_with_sick')} onChange={e => onChange('contact_with_sick', e.target.checked)} />}
            label="Had contact with a sick person in the last 21 days"
          />
          <FormControlLabel
            control={<Checkbox checked={bool('visited_epidemic_area')} onChange={e => onChange('visited_epidemic_area', e.target.checked)} />}
            label="Visited an area affected by an epidemic"
          />
          {bool('visited_epidemic_area') && (
            <TextField
              fullWidth size="small"
              label="Epidemic name and location"
              value={str('epidemic_details')}
              onChange={e => onChange('epidemic_details', e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      )}

      {/* ── Vaccination ── */}
      {tab === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={bool('yellow_fever_vaccinated')} onChange={e => onChange('yellow_fever_vaccinated', e.target.checked)} />}
              label="Vaccinated against Yellow Fever"
            />
          </Grid>
          {bool('yellow_fever_vaccinated') && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Yellow Fever Vaccination Date"
                type="date"
                value={str('yellow_fever_date')}
                onChange={e => onChange('yellow_fever_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" multiline rows={2}
              label="Other recent vaccinations"
              value={str('other_vaccinations')}
              onChange={e => onChange('other_vaccinations', e.target.value)}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HealthFormStep;
